import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import QueryBuilder from './QueryBuilder';
import { getCountByCategoryDetailedData, workspaceGetEnv, getonlyWorkspaces, getCostByCategoryDetailedData, getNewCountByCategoryDetailedData, getQueryTrendDetailedData, getDbuGroupDetailedData, getCatalogDetailedData, getFailedQueryDetailedData, getPeakQueryDetailedData, fetchSectorList } from 'service/service';
import moment from 'moment';
import PageWrapper from 'components/Common/PageWraper';
import FiltersBar from './FilterBar';
import { QueryRule, FieldOption, ENV_OPTIONS_DEFAULT } from 'utils/adb-common-util';

const renderCell = (key: string) => ({ row }: any) => (
  <div title={row?.[key]}>{row?.[key] || '--'}</div>
);

const renderDate = (key: string) => ({ row }: any) => {
  const d = row?.[key];
  return <div>{d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '--'}</div>;
};

const renderCost = (key: string) => ({ row }: any) => (
  <div style={{ textAlign: 'left' }}>{row?.[key] ?? '--'}</div>
);

const renderType = ({ row }: any) => {
  const type = row?.warehouse_type || '--';
  let color;
  if (type === 'PRO') {
    color = '#2196f3';
  } else if (type === 'CLASSIC') {
    color = '#ff9800';
  } else {
    color = '#4caf50';
  }
  return <div style={{ color, fontWeight: 600 }}>{type}</div>;
};

const createCol = (key: string, name: string, minWidth: number, options: any = {}) => ({
  key,
  name,
  minWidth,
  formatter: options.formatter || renderCell(key),
  sortable: options.sortable === undefined ? true : options.sortable,
});

function getColumns(navSearchSource: any) {
  const COL_MAP: Record<string, any[]> = {
    totalCountByCategory: [
      createCol('workspace_name', 'Workspace Name', 300),
      createCol('warehouse_name', 'Warehouse Name', 350),
      createCol('workspace_id', 'Workspace Id', 180),
      createCol('warehouse_id', 'Warehouse Id', 180),
      createCol('account_id', 'Account Id', 180, { formatter: renderCell('warehouse_id') }),
      createCol('warehouse_type', 'Warehouse Type', 140, { formatter: renderType }),
      createCol('warehouse_size', 'Warehouse Size', 140, { formatter: ({ row }: any) => <div>{row?.warehouse_size || '--'}</div> }),
      createCol('min_clusters', 'Min Clusters', 120, { formatter: renderCost('min_clusters') }),
      createCol('max_clusters', 'Max Clusters', 120, { formatter: renderCost('max_clusters') }),
      createCol('auto_stop_minutes', 'Auto Stop (mins)', 140, { formatter: renderCost('auto_stop_minutes') }),
      createCol('warehouse_channel', 'Channel', 120, { formatter: ({ row }: any) => <div>{row?.warehouse_channel || '--'}</div> }),
      createCol('change_time', 'Change Time', 180, { formatter: renderDate('change_time') }),
      createCol('delete_time', 'Delete Time', 180, { formatter: renderDate('delete_time') }),
      createCol('billing_info', 'Billing Info', 250, { sortable: false }),
      createCol('project_name', 'Project Name', 180, { sortable: false }),
      createCol('business_sector', 'Business Sector', 140, { formatter: ({ row }: any) => <div>{row?.business_sector || '--'}</div>, sortable: false }),
      createCol('business_contact', 'Business Contact', 220, { sortable: false }),
      createCol('ProjectNumber', 'Project Number', 220, { sortable: false }),
    ],
    costGroup: [
      createCol('warehouse_id', 'Warehouse ID', 180),
      createCol('sku_name', 'SKU Name', 250),
      createCol('usage_end_time', 'Usage End Time', 200, { formatter: renderDate('usage_end_time') }),
      createCol('cloud', 'Cloud Provider', 150, { formatter: ({ row }: any) => <div>{row?.cloud || '--'}</div> }),
      createCol('usage_usd', 'Usage Cost (USD)', 160, { formatter: renderCost('usage_usd') }),
      createCol('workspace_name', 'Workspace Name', 300),
      createCol('warehouse_name', 'Warehouse Name', 300),
      createCol('warehouse_type', 'Warehouse Type', 160, { formatter: renderType }),
      createCol('usage_quantity', 'DBU', 180, { formatter: renderCost('usage_quantity') }),
      createCol('usage_unit', 'Usage Unit', 140, { formatter: ({ row }: any) => <div>{row?.usage_unit || '--'}</div> }),
      createCol('workspace_id', 'Workspace ID', 180),
    ],
    warehouseByCategory: [
      createCol('workspace_id', 'Workspace ID', 180),
      createCol('warehouse_type', 'Warehouse Type', 180),
      createCol('account_id', 'Account ID', 180),
      createCol('auto_stop_minutes', 'Auto Stop Minutes', 180),
      createCol('change_time', 'Change Time', 180),
      createCol('warehouse_name', 'Warehouse Name', 180),
      createCol('warehouse_channel', 'Warehouse Channel', 180),
      createCol('warehouse_size', 'Warehouse Size', 180),
      createCol('min_clusters', 'Min Clusters', 180),
      createCol('project_name', 'Project Name', 180, { sortable: false }),
      createCol('business_sector', 'Business Sector', 140, { sortable: false, formatter: ({ row }: any) => <div>{row?.business_sector || '--'}</div> }),
      createCol('business_contact', 'Business Contact', 220, { sortable: false }),
      createCol('ProjectNumber', 'Project Number', 220, { sortable: false }),
      createCol('warehouse_id', 'Warehouse ID', 180),
      createCol('max_clusters', 'Max Clusters', 180),
      createCol('delete_time', 'Delete Time', 180),
      createCol('workspace_name', 'Workspace Name', 180),
    ],
    queryVolumeTrend: [
      createCol('total_queries', 'Statement ID Count', 180),
      createCol('query_source_type', 'Query Source Type', 180),
      createCol('start_time', 'Start Time', 180),
      createCol('query_attributed_dollars_estimation', 'Query Attributed Dollars Estimation', 220, { formatter: renderCost('total_cost') }),
      createCol('executed_by', 'Executed By', 220),
      createCol('workspace_id', 'Workspace ID', 180),
      createCol('warehouse_id', 'Warehouse ID', 180),
      createCol('end_time', 'End Time', 180),
      createCol('query_work_duration_seconds', 'Query Work Duration Seconds', 220, { formatter: renderCost('total_work_duration_seconds') }),
      createCol('warehouse_name', 'Warehouse Name', 200),
      createCol('warehouse_type', 'Warehouse Type', 180),
      createCol('execution_status', 'Execution Status', 180),
      createCol('duration_seconds', 'Duration Seconds', 180, { formatter: renderCost('total_duration_seconds') }),
      createCol('query_attributed_dbus_estimation', 'Query Attributed DBUs Estimation', 220, { formatter: renderCost('total_dbus') }),
      createCol('catalog_name', 'Catalog Name', 200),
      createCol('workspace_name', 'Workspace Name', 200),
    ],
    userGroup: [
      createCol('query_attributed_dbus_estimation', 'Query Attributed Dbus Estimation', 220, { formatter: renderCost('total_dbus') }),
      createCol('query_source_type', 'Query Source Type', 180),
      createCol('warehouse_name', 'Warehouse Name', 200),
      createCol('execution_status', 'Execution Status', 180),
      createCol('start_time', 'Start Time', 180),
      createCol('query_attributed_dollars_estimation', 'Query Attributed Dollars Estimation', 220, { formatter: renderCost('total_cost') }),
      createCol('total_queries', 'Statement ID Count', 200),
      createCol('workspace_id', 'Workspace ID', 180),
      createCol('end_time', 'End Time', 180),
      createCol('executed_by', 'Executed By', 220),
      createCol('duration_seconds', 'Duration Seconds', 180, { formatter: renderCost('total_duration_seconds') }),
      createCol('query_work_duration_seconds', 'Query Work Duration Seconds', 220, { formatter: renderCost('total_work_duration_seconds') }),
      createCol('catalog_name', 'Catalog Name', 200),
      createCol('warehouse_id', 'Warehouse ID', 200),
    ],
    catalogGroup: [
      createCol('execution_status', 'Execution Status', 180),
      createCol('end_time', 'End Time', 180),
      createCol('duration_seconds', 'Duration Seconds', 180, { formatter: renderCost('total_duration_seconds') }),
      createCol('query_attributed_dbus_estimation', 'Query Attributed Dbus Estimation', 220, { formatter: renderCost('total_dbus') }),
      createCol('warehouse_name', 'Warehouse Name', 200),
      createCol('executed_by', 'Executed By', 220),
      createCol('workspace_id', 'Workspace ID', 180),
      createCol('query_work_duration_seconds', 'Query Work Duration Seconds', 220, { formatter: renderCost('total_work_duration_seconds') }),
      createCol('query_attributed_dollars_estimation', 'Query Attributed Dollars Estimation', 220, { formatter: renderCost('total_cost') }),
      createCol('total_queries', 'Statement ID Count', 200),
      createCol('query_source_type', 'Query Source Type', 180),
      createCol('warehouse_id', 'Warehouse ID', 200),
      createCol('start_time', 'Start Time', 180),
      createCol('catalog_name', 'Catalog Name', 200),
    ],
    failedqueriesdetails: [
      createCol('executed_by', 'Executed By', 220),
      createCol('warehouse_id', 'Warehouse ID', 200),
      createCol('end_time', 'End Time', 180),
      createCol('duration_seconds', 'Duration Seconds', 180, { formatter: renderCost('total_duration_seconds') }),
      createCol('query_attributed_dollars_estimation', 'Query Attributed Dollars Estimation', 220, { formatter: renderCost('total_cost') }),
      createCol('query_source_type', 'Query Source Type', 200),
      createCol('start_time', 'Start Time', 180),
      createCol('query_attributed_dbus_estimation', 'Query Attributed Dbus Estimation', 220, { formatter: renderCost('total_dbus') }),
      createCol('total_queries', 'Statement ID Count', 200),
      createCol('warehouse_name', 'Warehouse Name', 200),
      createCol('query_work_duration_seconds', 'Query Work Duration Seconds', 220, { formatter: renderCost('total_work_duration_seconds') }),
      createCol('catalog_name', 'Catalog Name', 200),
      createCol('execution_status', 'Execution Status', 180),
      createCol('workspace_id', 'Workspace ID', 200),
    ],
    peakConcurrencyQueryCount: [
      createCol('workspace_id', 'Workspace ID', 200),
      createCol('query_attributed_dollars_estimation', 'Query Attributed Dollars Estimation', 220, { formatter: renderCost('total_cost') }),
      createCol('warehouse_id', 'Warehouse ID', 200),
      createCol('start_time', 'Start Time', 180),
      createCol('total_queries', 'Statement ID Count', 200),
      createCol('warehouse_name', 'Warehouse Name', 200),
      createCol('executed_by', 'Executed By', 220),
      createCol('query_work_duration_seconds', 'Query Work Duration Seconds', 220, { formatter: renderCost('total_work_duration_seconds') }),
      createCol('query_attributed_dbus_estimation', 'Query Attributed Dbus Estimation', 220, { formatter: renderCost('total_dbus') }),
      createCol('catalog_name', 'Catalog Name', 200),
      createCol('execution_status', 'Execution Status', 180),
      createCol('query_source_type', 'Query Source Type', 200),
      createCol('end_time', 'End Time', 180),
      createCol('duration_seconds', 'Duration Seconds', 180, { formatter: renderCost('total_duration_seconds') }),
    ]
  };

  if (['totalCostByCategory', 'costSqlWarehouse', 'warehouseCostTrend', 'dbuUsageSqlWarehouses'].includes(navSearchSource)) {
    return COL_MAP.costGroup;
  }
  if (['userByDbuUsage', 'userByCost'].includes(navSearchSource)) {
    return COL_MAP.userGroup;
  }
  if (['catalogsCount', 'catalogsCost'].includes(navSearchSource)) {
    return COL_MAP.catalogGroup;
  }

  return COL_MAP[navSearchSource] || [];
}

const SERVICE_MAP: Record<string, any> = {
  totalCountByCategory: getCountByCategoryDetailedData,
  totalCostByCategory: getCostByCategoryDetailedData,
  costSqlWarehouse: getCostByCategoryDetailedData,
  warehouseCostTrend: getCostByCategoryDetailedData,
  dbuUsageSqlWarehouses: getCostByCategoryDetailedData,
  warehouseByCategory: getNewCountByCategoryDetailedData,
  queryVolumeTrend: getQueryTrendDetailedData,
  userByDbuUsage: getDbuGroupDetailedData,
  userByCost: getDbuGroupDetailedData,
  catalogsCount: getCatalogDetailedData,
  catalogsCost: getCatalogDetailedData,
  failedqueriesdetails: getFailedQueryDetailedData,
  peakConcurrencyQueryCount: getPeakQueryDetailedData,
};

const QB_FILTER_MAP: Record<string, string> = {
  'Workspace Name': 'workspace_name',
  'Warehouse Type': 'warehouse_type',
  'Warehouse Name': 'warehouse_name',
  'User Executed By': 'executed_by',
  'Catalog Name': 'catalog_name',
};

const SQLDetailsPage = () => {
  const routerLocation = useLocation();
  const navigate = useNavigate();

  // Get data from navigation state
  interface NavigationState {
    dateLabel?: string;
    apiData?: any;
    type?: 'year' | 'month' | 'week' | 'day';
    startDate?: string;
    endDate?: string;
    env?: string;
    sector?: string;
    location?: string;
    selectedDateInString?: string;
    workspaceName?: string;
    warehouseType?: string;
    computeType?: string;
    jobType?: string;
    resultState?: string;
    filters?: string;
    searchSource?: string;
    warehouseName?: string;
    userExecutedBy?: string;
    catalogName?: string;
    warehouseNameOptions?: string[];
    userExecutedByOptions?: string[];
    catalogNameFilterOptions?: string[];
    detailedTableTitle?: string;
    clickedItemName?: string;
    pageHeader?: string;
    pageSubHeader?: string;
    detailedTableType?: string;
    contentName?: string;
  }

  // Get data from navigation state
  const navigationState = routerLocation.state as NavigationState | null;

  const {
    dateLabel = navigationState?.detailedTableTitle || 'Peak Concurrency Query Count',
    apiData,
    type = 'day',
    startDate: navStartDate,
    endDate: navEndDate,
    env: navEnv,
    sector: navSector,
    location: navLocation,
    selectedDateInString: navSelectedDate,
    workspaceName: navWorkspaceName,
    warehouseType: navWarehouseType,
    warehouseName: navWarehouseName,
    userExecutedBy: navUserExecutedBy,
    catalogName: navCatalogName,
    computeType: navComputeType,
    jobType: navJobType,
    resultState,
    searchSource: navSearchSource,
    filters: navFilters,
    warehouseNameOptions: navWarehouseNameOptions,
    userExecutedByOptions: navUserExecutedByOptions,
    catalogNameFilterOptions: navCatalogNameFilterOptions,
    clickedItemName,
    pageHeader,
    pageSubHeader,
    detailedTableType,
    detailedTableTitle,
    contentName: navContentName
  } = (navigationState || {});

  // Filter states
  const [sector, setSector] = useState<string>(navSector || 'all');
  const [env, setEnv] = useState(navEnv || 'both');
  const [envOptionsState, setEnvOptionsState] = useState<{ label: string; value: string }[]>(ENV_OPTIONS_DEFAULT);
  const [, setLoaderEnv] = useState(false);
  const initialEnvAppliedRef = useRef(false);
  const envRef = useRef(env);
  useEffect(() => { envRef.current = env; }, [env]);
  const [locationFilter, setLocationFilter] = useState(navLocation || 'South Central US');
  const [sectorName, setSectorName] = useState(navSector || 'all');
  const [startDate, setStartDate] = useState(
    navStartDate || moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(
    navEndDate || moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD')
  );
  const [selectedDateInString, setSelectedDateInString] = useState(navSelectedDate || '7d');

  // API state
  const [tableRecords, setTableRecords] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({
    totalElements: 0,
    totalPages: 0
  });

  // Query Builder state
  const [queryBuilderFilters, setQueryBuilderFilters] = useState<QueryRule[]>([]);
  const [queryBuilderKey, setQueryBuilderKey] = useState(0);
  const [workspaceOptions, setWorkspaceOptions] = useState<FieldOption[]>([
    { label: 'All', value: 'all' }
  ]);

  const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });
  const [, setFilterInfo] = useState([]);
  const [sectorList, setSectorList] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Derived filters for Breadcrumbs
  const currentWarehouseType = queryBuilderFilters.find(f => f.field === 'Warehouse Type')?.value ?? (isInitialized ? undefined : navWarehouseType);
  const currentWorkspaceName = queryBuilderFilters.find(f => f.field === 'Workspace Name')?.value ?? (isInitialized ? undefined : navWorkspaceName);
  const currentWarehouseName = queryBuilderFilters.find(f => f.field === 'Warehouse Name')?.value ?? (isInitialized ? undefined : navWarehouseName);
  const currentUserExecutedBy = queryBuilderFilters.find(f => f.field === 'User Executed By')?.value ?? (isInitialized ? undefined : navUserExecutedBy);
  const currentCatalogName = queryBuilderFilters.find(f => f.field === 'Catalog Name')?.value ?? (isInitialized ? undefined : navCatalogName);

  const currentFiltersArray = queryBuilderFilters
    .filter(f => {
      const v = f.value?.toString().toLowerCase();
      return v && v !== 'all' && v !== '';
    })
    .map(f => {
      let field = f.field;
      if (field === 'Workspace Name') field = 'workspace_name';
      else if (field === 'Warehouse Type') field = 'warehouse_type';
      else if (field === 'Warehouse Name') field = 'warehouse_name';
      else if (field === 'User Executed By') field = 'executed_by';
      else if (field === 'Catalog Name') field = 'catalog_name';
      return { operator: f.operator, field, value: f.value };
    });
  const currentFiltersStr = currentFiltersArray.length > 0 ? JSON.stringify(currentFiltersArray) : '';

  const showWarehouseName = ['dbuUsageSqlWarehouses', 'costSqlWarehouse', 'totalCostByCategory', 'warehouseByCategory', 'warehouseCostTrend'].includes(navSearchSource || '');
  const showExecutiveName = ['userByDbuUsage', 'userByCost'].includes(navSearchSource || '');
  const showCatalogName = ['catalogsCount', 'catalogsCost'].includes(navSearchSource || '');

  const formatTitle = (base: string, suffix?: string) => (suffix ? `${base} - ${suffix}` : base);

  const breadCrumbs: any = {
    default: {},
    totalCountByCategory: {
      text: 'Total Count By Category',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Total Count By Category', currentWarehouseType),
        searchSource: 'totalCountByCategory',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Total Count By Category', currentWarehouseType),
        detailedTableType: 'sql-warehouse-count-table',
        filters: currentFiltersStr,
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    totalCostByCategory: {
      text: 'Total Cost By Category',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Total Cost By Category', currentWarehouseType),
        searchSource: 'totalCostByCategory',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Total Cost By Category', currentWarehouseType),
        detailedTableType: 'sql-warehouse-cost-table',
        filters: currentFiltersStr,
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    costSqlWarehouse: {
      text: 'Cost By SQL Warehouse',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Cost By SQL Warehouse', currentWarehouseName),
        searchSource: 'costSqlWarehouse',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Cost By SQL Warehouse', currentWarehouseName),
        detailedTableType: 'sql-warehouse-cost-table',
        filters: currentFiltersStr,
        userExecutedByOptions: navUserExecutedByOptions || [],
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    warehouseByCategory: {
      text: 'Warehouse By Category',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Warehouse By Category', currentWarehouseType),
        searchSource: 'warehouseByCategory',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Warehouse By Category', currentWarehouseType),
        detailedTableType: 'warehouse-by-category-detail',
        filters: currentFiltersStr,
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    warehouseCostTrend: {
      text: 'Warehouse Cost Trend',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Warehouse Cost Trend', currentWarehouseType),
        searchSource: 'warehouseCostTrend',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Warehouse Cost Trend', currentWarehouseType),
        detailedTableType: 'warehouse-cost-trend-detail',
        filters: currentFiltersStr,
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    dbuUsageSqlWarehouses: {
      text: 'DBU Usage By SQL Warehouses',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('DBU Usage By SQL Warehouses', currentWarehouseName),
        searchSource: 'dbuUsageSqlWarehouses',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('DBU Usage By SQL Warehouses', currentWarehouseName),
        detailedTableType: 'dbu-usage-sql-warehouses-detail',
        filters: currentFiltersStr,
        warehouseNameOptions: navWarehouseNameOptions || [],
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    queryVolumeTrend: {
      text: 'Query Volume Trend',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Query Volume Trend', currentWarehouseType),
        searchSource: 'queryVolumeTrend',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Query Volume Trend', currentWarehouseType),
        detailedTableType: 'query-volume-trend-detail',
        filters: currentFiltersStr,
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    userByDbuUsage: {
      text: 'User By DBU Usage',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('User By DBU Usage', currentUserExecutedBy),
        searchSource: 'userByDbuUsage',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('User By DBU Usage', currentUserExecutedBy),
        detailedTableType: 'user-by-dbu-usage-detail',
        filters: currentFiltersStr,
        warehouseNameOptions: navWarehouseNameOptions || [],
        userExecutedByOptions: navUserExecutedByOptions || [],
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    userByCost: {
      text: 'User By Cost',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('User By Cost', currentUserExecutedBy),
        searchSource: 'userByCost',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('User By Cost', currentUserExecutedBy),
        detailedTableType: 'user-by-cost-detail',
        filters: currentFiltersStr,
        warehouseNameOptions: navWarehouseNameOptions || [],
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    catalogsCount: {
      text: 'Catalogs Count',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Catalogs Count', currentCatalogName),
        searchSource: 'catalogsCount',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Catalogs Count', currentCatalogName),
        detailedTableType: 'catalogs-count-detail',
        filters: currentFiltersStr,
        catalogNameFilterOptions: navCatalogNameFilterOptions || [],
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    catalogsCost: {
      text: 'Catalogs Cost',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: formatTitle('Catalogs Cost', currentCatalogName),
        searchSource: 'catalogsCost',
        contentName: 'ALL',
        detailedTableTitle: formatTitle('Catalogs Cost', currentCatalogName),
        detailedTableType: 'catalogs-cost-detail',
        filters: currentFiltersStr,
        catalogNameFilterOptions: navCatalogNameFilterOptions || [],
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    failedqueriesdetails: {
      text: 'Failed Queries Details',
      link: '/databricksADB/performance-dashboard',
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString,
        pageHeader: 'DBSQL Performance',
        pageSubHeader: 'Failed Queries Details',
        searchSource: 'failedqueriesdetails',
        contentName: 'ALL',
        detailedTableTitle: 'Failed Queries Details',
        detailedTableType: 'failed-queries-details',
        filters: currentFiltersStr,
        warehouseName: currentWarehouseName,
        warehouseType: currentWarehouseType,
        userExecutedBy: currentUserExecutedBy,
        catalogName: currentCatalogName,
        workspaceName: currentWorkspaceName
      }
    },
    peakConcurrencyQueryCount: {}
  };

  const fetchSectors = async () => {
    const resp = await fetchSectorList({});
    if (resp?.success) {
      setSectorList(resp?.data || []);
    }
  };

  useEffect(() => {
    fetchSectors()
  }, [])

  const updateHistoryState = (overrides: Partial<{
    startDate: string;
    endDate: string;
    env: string;
    sector: string;
    location: string;
    selectedDateInString: string;
    workspaceName: string;
    computeType: string;
    filters?: any;
    contentName?: string;
    warehouseName?: string;
    warehouseType?: string;
    userExecutedBy?: string;
    catalogName?: string;
    searchSource?: string;
  }>) => {
    const newState = {
      dateLabel,
      apiData,
      type,
      startDate,
      endDate,
      env,
      sector,
      location: locationFilter,
      selectedDateInString,
      workspaceName: currentWorkspaceName,
      computeType: navComputeType,
      filters: currentFiltersStr,
      searchSource: navSearchSource,
      warehouseType: currentWarehouseType,
      warehouseName: currentWarehouseName,
      userExecutedBy: currentUserExecutedBy,
      catalogName: currentCatalogName,
      jobType: navJobType,
      resultState,
      warehouseNameOptions: navWarehouseNameOptions,
      userExecutedByOptions: navUserExecutedByOptions,
      catalogNameFilterOptions: navCatalogNameFilterOptions,
      pageHeader,
      pageSubHeader,
      detailedTableType,
      detailedTableTitle,
      contentName: navContentName,
      ...overrides
    };
    navigate(routerLocation.pathname, { state: newState, replace: true });
  };

  useEffect(() => {
    // Only update history if the current filters in state are different from history state
    // AND if we have already initialized from navigation state.
    if (isInitialized && currentFiltersStr !== (navFilters || '')) {
      updateHistoryState({});
    }
  }, [currentFiltersStr, navFilters, isInitialized]);

  // Update filters when navigation state changes
  useEffect(() => {
    if (navigationState) {
      if (navStartDate) setStartDate(navStartDate);
      if (navEndDate) setEndDate(navEndDate);
      if (navEnv) setEnv(navEnv);
      if (navSector) {
        setSector(navSector);
        setSectorName(navSector);
      }
      if (navLocation) setLocationFilter(navLocation);
      if (navSelectedDate) setSelectedDateInString(navSelectedDate);
    }
  }, []);

  const getEnvFetch = async (loc: string) => {
    try {
      setLoaderEnv(true);
      const res = await workspaceGetEnv(loc);
      const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
      setEnvOptionsState(options);

      if (!initialEnvAppliedRef.current && navEnv) {
        setEnv(navEnv);
        initialEnvAppliedRef.current = true;
      } else {
        const availableValues = options.map(o => o.value);
        const currentEnv = envRef.current;
        if (!availableValues.includes(currentEnv)) {
          const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
          setEnv(fallback);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoaderEnv(false);
    }
  };

  useEffect(() => {
    getEnvFetch(locationFilter);
  }, [locationFilter]);

  // Reset filters when location or env changes for specific search sources
  useEffect(() => {
    if (isInitialized && ['dbuUsageSqlWarehouses', 'costSqlWarehouse'].includes(navSearchSource || '')) {
      setQueryBuilderFilters([]);
    }
  }, [locationFilter, env, navSearchSource]);

  // Fetch workspace options
  const buildWorkspaceMap = (data: any[]) => {
    const workspaceMap = new Map<string, { name: string; id: string }>();
    data.forEach((workspace: any) => {
      if (workspace.workspace_id && workspace.workspace_name) {
        workspaceMap.set(workspace.workspace_id, {
          name: workspace.workspace_name,
          id: workspace.workspace_id
        });
      }
    });
    return workspaceMap;
  };

  const ensureNavWorkspaceIncluded = (workspaceMap: Map<string, { name: string; id: string }>) => {
    if (navWorkspaceName && navWorkspaceName !== 'all') {
      const existing = Array.from(workspaceMap.values()).find(
        w => w.name === navWorkspaceName || w.id === navWorkspaceName
      );
      if (!existing) {
        workspaceMap.set(navWorkspaceName, {
          name: navWorkspaceName,
          id: navWorkspaceName
        });
      }
    }
  };

  const createWorkspaceOptions = (workspaceMap: Map<string, { name: string; id: string }>) => {
    const workspaceList = Array.from(workspaceMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    return [
      { label: 'All', value: 'all' },
      ...workspaceList.map(w => ({
        label: w.name,
        value: w.id,
        workspaceName: w.name,
        workspaceId: w.id
      }))
    ];
  };

  const setFallbackWorkspaceOptions = () => {
    if (navWorkspaceName && navWorkspaceName !== 'all') {
      setWorkspaceOptions([
        { label: 'All', value: 'all' },
        {
          label: navWorkspaceName,
          value: navWorkspaceName,
          workspaceName: navWorkspaceName,
          workspaceId: navWorkspaceName
        }
      ]);
    }
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const resp = await getonlyWorkspaces({
          startDate,
          endDate,
          env: env && env !== 'both' ? env : '',
          sector: sector && sector !== 'all' ? sector : '',
          location: locationFilter && locationFilter !== 'ALL' ? locationFilter : '',
          exportPage: 1,
          exportPageSize: 1000,
          filters: [],
          selectedDateInString
        });

        if (resp?.success && resp?.data && Array.isArray(resp.data)) {
          const workspaceMap = buildWorkspaceMap(resp.data);
          ensureNavWorkspaceIncluded(workspaceMap);
          const options = createWorkspaceOptions(workspaceMap);
          setWorkspaceOptions(options);
        } else {
          setFallbackWorkspaceOptions();
        }
      } catch (error) {
        console.error(error);
        setFallbackWorkspaceOptions();
      }
    };

    fetchFilterOptions();
  }, [startDate, endDate, env, sector, locationFilter, selectedDateInString, navWorkspaceName]);

  const mapApiFieldToUiField = (field: string): string => {
    if (field === 'workspace_name' || field === 'workspace_id' || field === 'Workspace ID') {
      return 'Workspace Name';
    }
    if (field === 'warehouse_type') return 'Warehouse Type';
    if (field === 'warehouse_name') return 'Warehouse Name';
    if (field === 'executed_by') return 'User Executed By';
    if (field === 'catalog_name') return 'Catalog Name';
    return field;
  };

  const processNavFilters = (
    navFilters: any,
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    if (!navFilters) return;

    try {
      const parsedFilters = typeof navFilters === 'string' ? JSON.parse(navFilters) : navFilters;
      if (!Array.isArray(parsedFilters)) return;

      parsedFilters.forEach((f: any, index: number) => {
        const uiField = mapApiFieldToUiField(f.field);
        if (!addedFields.has(uiField)) {
          initialFilters.push({
            id: `saved-${index}`,
            operator: f.operator || 'AND',
            field: uiField,
            value: f.value
          });
          addedFields.add(uiField);
        }
      });
    } catch (error) {
      console.error('Failed to parse navFilters', error);
    }
  };

  const addFilterIfNotExists = (
    field: string,
    value: string,
    id: string,
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    if (!addedFields.has(field)) {
      initialFilters.push({
        id,
        operator: 'AND',
        field,
        value
      });
      addedFields.add(field);
    }
  };

  const addWorkspaceFilter = (
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    if (addedFields.has('Workspace Name')) return;

    let wsValue;
    if (navWorkspaceName && navWorkspaceName !== 'all') {
      wsValue = navWorkspaceName;
    } else if (navContentName && navContentName !== 'ALL') {
      wsValue = navContentName;
    } else {
      wsValue = 'all';
    }

    addFilterIfNotExists('Workspace Name', wsValue, '1', initialFilters, addedFields);
  };

  const addWarehouseTypeFilter = (
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    const validTypes = ['PRO', 'CLASSIC', 'SERVERLESS'];
    if (!addedFields.has('Warehouse Type') && validTypes.includes(navWarehouseType || '')) {
      addFilterIfNotExists(
        'Warehouse Type',
        navWarehouseType?.toUpperCase() || '',
        '3',
        initialFilters,
        addedFields
      );
    }
  };

  const addWarehouseNameFilter = (
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    if (!addedFields.has('Warehouse Name') && !!navWarehouseName && showWarehouseName) {
      addFilterIfNotExists('Warehouse Name', navWarehouseName, '4', initialFilters, addedFields);
    }
  };

  const addUserExecutedByFilter = (
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    if (!addedFields.has('User Executed By') && !!navUserExecutedBy && showExecutiveName) {
      addFilterIfNotExists('User Executed By', navUserExecutedBy, '5', initialFilters, addedFields);
    }
  };

  const addCatalogNameFilter = (
    initialFilters: QueryRule[],
    addedFields: Set<string>
  ) => {
    if (!addedFields.has('Catalog Name') && !!navCatalogName && showCatalogName) {
      addFilterIfNotExists('Catalog Name', navCatalogName, '6', initialFilters, addedFields);
    }
  };

  useEffect(() => {
    if (isInitialized) return;

    const initialFilters: QueryRule[] = [];
    const addedFields = new Set<string>();

    processNavFilters(navFilters, initialFilters, addedFields);
    addWorkspaceFilter(initialFilters, addedFields);
    addWarehouseTypeFilter(initialFilters, addedFields);
    addWarehouseNameFilter(initialFilters, addedFields);
    addUserExecutedByFilter(initialFilters, addedFields);
    addCatalogNameFilter(initialFilters, addedFields);

    if (initialFilters.length > 0) {
      setQueryBuilderFilters(initialFilters);
      setQueryBuilderKey(prev => prev + 1);
      setPage(1);
    }
    setIsInitialized(true);
  }, [navWorkspaceName, navContentName, navFilters, navWarehouseType, navWarehouseName, navUserExecutedBy, navCatalogName, isInitialized]);

  const generatePayload = useCallback((isExport = false) => {
    const apiFilters: any[] = [];
    queryBuilderFilters.forEach(qbFilter => {
      const v = qbFilter.value?.toString().toLowerCase();
      if (!v || v === 'all' || v === '') return;
      const apiField = QB_FILTER_MAP[qbFilter.field];
      if (!apiField) return;

      let value = qbFilter.value;
      if (qbFilter.field === 'Workspace Name') {
        const match = workspaceOptions.find(opt => opt.label === qbFilter.value);
        if (match && match.value !== 'all') value = match.value;
        else return;
      }

      apiFilters.push({ operator: 'AND', field: apiField, value });
    });

    return {
      startDate,
      endDate,
      env: env === 'both' ? '' : env,
      sector: sector === 'all' ? '' : sector,
      location: locationFilter,
      selectedDateInString,
      page: isExport ? 1 : page,
      pageSize: isExport ? 10000 : pageSize,
      sortField: sortingInfo?.sortBy || undefined,
      sortOrder: sortingInfo?.sortDir || undefined,
      filters: apiFilters,
      ...(isExport ? { exportType: 'excel' } : {})
    };
  }, [queryBuilderFilters, workspaceOptions, startDate, endDate, env, sector, locationFilter, selectedDateInString, page, pageSize, sortingInfo]);

  const normalizeRecord = useCallback((r: any, index: number) => ({
    ...r,
    usage_quantity: r?.usage_quantity?.toFixed(2) || '--',
    id: r?.warehouse_id || `row-${index}`,
    workspace_id: r?.workspace_id || '--',
    workspace_name: r?.workspace_name || '--',
    warehouse_id: r?.warehouse_id || '--',
    warehouse_name: r?.warehouse_name || '--',
    warehouse_type: r?.warehouse_type || '--',
    warehouse_size: r?.warehouse_size || '--',
    warehouse_channel: r?.warehouse_channel || '--',
    min_clusters: r?.min_clusters ?? '--',
    max_clusters: r?.max_clusters ?? '--',
    auto_stop_minutes: r?.auto_stop_minutes ?? '--',
    change_time: r?.change_time || null,
    delete_time: r?.delete_time || null,
    account_id: r?.account_id || '--',
    tags: r?.tags || {},
    billing_info: r?.tags?.BillingInformation || '--',
    project_name: r?.tags?.ProjectName || '--',
    business_sector: r?.tags?.BusinessSector || '--',
    business_contact: r?.tags?.BusinessContact || '--',
    project_number: r?.tags?.ProjectNumber || '--',
    ProjectNumber: r?.tags?.ProjectNumber || '--'
  }), []);

  const fetchData = useCallback(async () => {
    setLoader(true);
    try {
      const payload = generatePayload();
      const service = SERVICE_MAP[navSearchSource || ''];
      if (!service) return;

      const resp = await service(payload);
      if (resp?.success) {
        const records = resp?.data?.docs || resp?.data?.data || [];
        setTableRecords(records.map(normalizeRecord));
        setPaginationData({
          totalElements: resp.data.pagination?.totalRecords || records.length,
          totalPages: resp.data.pagination?.totalPages || Math.ceil(records.length / pageSize)
        });
      } else {
        setTableRecords([]);
        setPaginationData({ totalElements: 0, totalPages: 0 });
      }
    } catch (error) {
      console.error(error);
      setTableRecords([]);
      setPaginationData({ totalElements: 0, totalPages: 0 });
    } finally {
      setLoader(false);
    }
  }, [generatePayload, navSearchSource, normalizeRecord, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportRequest = async () => {
    try {
      const payload = generatePayload(true);
      const service = SERVICE_MAP[navSearchSource || ''];
      if (service) await service(payload);
    } catch (err) {
      console.error('Export error', err);
    }
  };

  const handleSorting = (newSorting: { sortBy: string; sortDir: 'asc' | 'desc' | '' }) => {
    if (newSorting?.sortBy && (newSorting.sortDir === 'asc' || newSorting.sortDir === 'desc')) {
      setSortingInfo({
        sortBy: newSorting.sortBy,
        sortDir: newSorting.sortDir || ''
      });
    } else {
      setSortingInfo({ sortBy: '', sortDir: '' });
    }
  };

  const handleFilter = (filterInfo: any) => {
    setFilterInfo(filterInfo);
  };

  const handleRunQuery = (rules: QueryRule[]) => {
    setQueryBuilderFilters(rules);
    setPage(1);
  };
  // Updated table columns for warehouse configuration data
  const COST_COLS = ['Workspace ID', 'Workspace Name', 'Warehouse ID', 'Warehouse Name', 'SKU Name', 'Usage End Time', 'Cloud Provider', 'Usage Cost (USD)', 'Warehouse Type', 'DBU', 'Usage Unit'];
  const USER_COLS = ['Workspace ID', 'Warehouse ID', 'Warehouse Name', 'Statement ID', 'Catalog Name', 'Execution Status', 'Executed By', 'Start Time', 'End Time', 'Duration Seconds', 'Query Attributed Dbus Estimation', 'Query Source Type', 'Query Profile URL', 'Query Attributed Dollars Estimation', 'Query Work Duration Seconds'];
  const CATALOG_COLS = ['Workspace ID', 'Warehouse ID', 'Warehouse Name', 'Execution Status', 'End Time', 'Duration Seconds', 'Query Attributed Dbus Estimation', 'Executed By', 'Query Work Duration Seconds', 'Query Attributed Dollars Estimation', 'Statement ID', 'Query Source Type', 'Start Time', 'Query Profile URL', 'Catalog Name'];

  const defaultColumnNames: any = {
    totalCountByCategory: ['Workspace Id', 'Workspace Name', 'Warehouse Id', 'Warehouse Name', 'Account Id', 'Warehouse Type', 'Warehouse Size', 'Min Clusters', 'Max Clusters', 'Auto Stop (mins)', 'Channel', 'Change Time', 'Delete Time', 'Billing Info', 'Project Name', 'Business Sector', 'Business Contact', 'Project Number'],
    totalCostByCategory: COST_COLS,
    costSqlWarehouse: COST_COLS,
    warehouseByCategory: ['Workspace ID', 'Workspace Name', 'Warehouse ID', 'Warehouse Name', 'Warehouse Type', 'Account ID', 'Auto Stop Minutes', 'Change Time', 'Warehouse Channel', 'Warehouse Size', 'Min Clusters', 'Business Contact', 'Billing Information', 'Business Sector', 'Project Number', 'Project Name', 'Max Clusters', 'Delete Time'],
    warehouseCostTrend: COST_COLS,
    dbuUsageSqlWarehouses: COST_COLS,
    queryVolumeTrend: ['Workspace ID', 'Workspace Name', 'Warehouse ID', 'Warehouse Name', 'Warehouse Type', 'Statement ID', 'Catalog Name', 'Query Attributed Dollars Estimation', 'Execution Status', 'Executed By', 'Start Time', 'End Time', 'Query Source Type', 'Duration Seconds', 'Query Work Duration Seconds', 'Query Profile URL', 'Query Attributed Dbus Estimation'],
    userByDbuUsage: USER_COLS,
    userByCost: USER_COLS,
    catalogsCount: CATALOG_COLS,
    catalogsCost: CATALOG_COLS,
    failedqueriesdetails: ['Workspace ID', 'Warehouse ID', 'Warehouse Name', 'Statement ID', 'Start Time', 'End Time', 'Duration Seconds', 'Query Attributed Dollars Estimation', 'Query Source Type', 'Catalog Name', 'Execution Status', 'Executed By', 'Query Profile URL', 'Query Attributed Dbus Estimation', 'Query Work Duration Seconds'],
    peakConcurrencyQueryCount: ['Workspace ID', 'Warehouse ID', 'Warehouse Name', 'Statement ID', 'Executed By', 'Start Time', 'End Time', 'Query Work Duration Seconds', 'Query Source Type', 'Duration Seconds', 'Execution Status', 'Query Attributed Dbus Estimation', 'Catalog Name', 'Query Attributed Dollars Estimation', 'Query Profile URL']
  };

  const getActiveFilterName = () => {
    const warehouseName = queryBuilderFilters.find(f => f.field === 'Warehouse Name');
    if (warehouseName && warehouseName.value && warehouseName.value !== 'all') return warehouseName.value;

    const catalogName = queryBuilderFilters.find(f => f.field === 'Catalog Name');
    if (catalogName && catalogName.value && catalogName.value !== 'all') return catalogName.value;

    const userExecutedBy = queryBuilderFilters.find(f => f.field === 'User Executed By');
    if (userExecutedBy && userExecutedBy.value && userExecutedBy.value !== 'all') return userExecutedBy.value;

    const warehouseType = queryBuilderFilters.find(f => f.field === 'Warehouse Type');
    if (warehouseType && warehouseType.value && warehouseType.value !== 'all') return warehouseType.value;

    return clickedItemName;
  };

  const getFormattedTitle = () => {
    const activeName = getActiveFilterName();
    let title = dateLabel;
    if (activeName && !title.includes(activeName)) {
      title = `${activeName} - ${title}`;
    }
    return title;
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleDateSelect = (stDt: string, edDt: string, selectedDate?: string) => {
    const selected = selectedDate || 'Hrs';
    setSelectedDateInString(selected);

    const yesterday = moment().subtract(1, 'day');
    const end = yesterday.clone().endOf('day');
    let start;

    if (selected === 'custom') {
      start = moment(stDt).startOf('day');
      const customEnd = moment(edDt).endOf('day');
      setStartDate(start.format('YYYY-MM-DD'));
      setEndDate(customEnd.format('YYYY-MM-DD'));
      setPage(1);
      updateHistoryState({ startDate: start.format('YYYY-MM-DD'), endDate: customEnd.format('YYYY-MM-DD'), selectedDateInString: selected });
      return;
    }

    const dateOptions: Record<string, [number, moment.unitOfTime.DurationConstructor]> = {
      'Hrs': [0, 'days'],
      '7d': [6, 'days'],
      '30d': [29, 'days'],
      '1M': [1, 'month'],
      '3M': [3, 'months'],
      '6M': [6, 'months'],
      '1Y': [1, 'year'],
    };

    if (dateOptions[selected]) {
      const [amt, unit] = dateOptions[selected];
      start = yesterday.clone().subtract(amt, unit).startOf('day');
    } else {
      start = moment(stDt).startOf('day');
    }

    setStartDate(start.format('YYYY-MM-DD'));
    setEndDate(end.format('YYYY-MM-DD'));
    setPage(1);
    updateHistoryState({ startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD'), selectedDateInString: selected });
  };

  const handleDashboardClick = () => {
    navigate('/databricksADB/dbsqlperfomance', {
      state: {
        startDate,
        endDate,
        env,
        sector: sectorName,
        location: locationFilter,
        selectedDateInString
      }
    });
  };

  const renderTableContent = () => {
    if (loader) {
      return <LoaderComponent pt={20} />;
    }

    if (tableRecords.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            backgroundColor: 'white',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            mx: 2
          }}
        >
          <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>
            No Data Available
          </Box>
          <Box sx={{ fontSize: '0.9rem', color: '#999' }}>
            No warehouse configuration data found for the selected filters.
          </Box>
          <Box sx={{ fontSize: '0.85rem', color: '#999', mt: 2 }}>
            Date Range: {startDate} to {endDate}
          </Box>
          <Box sx={{ fontSize: '0.85rem', color: '#999' }}>
            Location: {locationFilter} | Environment: {env} | Sector: {sector}
            {navWorkspaceName && ` | Workspace: ${navWorkspaceName}`}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 2 }}>
        <CustomizedDataGrid
          title={`${getFormattedTitle()} (${paginationData.totalElements.toLocaleString()} records)`}
          tableColumns={getColumns(navSearchSource || '')}
          tableData={tableRecords}
          tableType="adb-sql-warehouse-table"
          defaultColumnNames={defaultColumnNames[navSearchSource || 'default'] || []}
          page={page}
          pageSize={pageSize}
          paginationData={paginationData}
          listLength={tableRecords.length}
          handlepageSizeChange={handlePageSizeChange}
          handlePageChange={handlePageChange}
          isDateRangeFilter={false}
          isDisabled={false}
          isFetching={loader}
          isSortIcon={true}
          showExport={true}
          exportTableData={handleExportRequest}
          onSorting={handleSorting}
          currSorting={sortingInfo}
          onFilterCheck={handleFilter}
        />
      </Box>
    );
  };

  const handleCategoryClick = () => {
    const bc = breadCrumbs[navSearchSource || 'default'];
    if (bc && bc.link) {
      navigate(bc.link, { state: bc.state });
    }
  };

  return (
    <PageWrapper>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
        <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: 600, color: '#003771' }}>
          <span style={{ cursor: 'pointer', color: '#003771' }} onClick={handleDashboardClick}>
            DBSQL Performance Dashboard
          </span>
          {navSearchSource === 'peakConcurrencyQueryCount' ? (
            <>
              {' > '}
              <span>Peak Concurrency Query Details</span>
            </>
          ) : (
            <>
              {breadCrumbs[navSearchSource || 'default']?.text && (
                <>
                  {' > '}
                  <span style={{ cursor: 'pointer', color: '#003771' }} onClick={handleCategoryClick}>
                    {breadCrumbs[navSearchSource || 'default'].text}
                  </span>
                </>
              )}
              {(getActiveFilterName() || getFormattedTitle()) && (
                <>
                  {' > '}
                  <span>
                    {getActiveFilterName() || getFormattedTitle()}
                  </span>
                </>
              )}
            </>
          )}
          {((selectedDateInString === 'customDateRange' || selectedDateInString === 'custom')
            && (startDate && endDate && startDate !== endDate)) && (
              <span style={{ marginLeft: 8, color: '#003771', fontSize: '0.95rem' }}>
                ({`${startDate} to ${endDate}`})
              </span>
            )}
        </Box>
        <Box sx={{ position: 'sticky', top: 64, zIndex: 10 }}>
          <PageHeader
            title=""
            hasEllipsis={false}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', width: '100%' }}>
              <FiltersBar
                location={locationFilter}
                setLocation={setLocationFilter}
                env={env}
                setEnv={setEnv}
                envOptions={envOptionsState}
                setEnvOptions={setEnvOptionsState}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleDateSelect={handleDateSelect}
                selectedDateInString={selectedDateInString}
                setSelectedDateInString={setSelectedDateInString}
                sectorName={sectorName}
                setSectorName={(v) => {
                  const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
                  setSectorName(resolved);
                  setSector(resolved);
                }}
                updateHistoryState={updateHistoryState}
                sectorOptionsList={sectorList}
                searchSource={navSearchSource}
              />
            </Box>
          </PageHeader>
        </Box>
      </Stack>

      <Box sx={{ px: 2, mb: 2 }}>
        <QueryBuilder
          key={queryBuilderKey}
          title="Filter Options"
          sectorOptions={workspaceOptions}
          warehouseTypeOptions={[
            { label: "PRO", value: "PRO" },
            { label: "CLASSIC", value: "CLASSIC" },
            { label: "SERVERLESS", value: "SERVERLESS" }
          ]}
          locationOptions={[]}
          teamFlagOptions={[]}
          onRunQuery={handleRunQuery}
          showQueryPreference={false}
          initialFilters={queryBuilderFilters}
          hideComputeType={true}
          isJob={false}
          isSQL={true}
          showWarehouseName={showWarehouseName}
          showExecutiveName={showExecutiveName}
          showCatalogName={showCatalogName}
          warehouseNameOptions={
            (navWarehouseNameOptions || []).map((name) => ({ label: name, value: name }))
          }
          catalogNameFilterOptions={
            (navCatalogNameFilterOptions || []).map((name) => ({ label: name, value: name }))
          }
          userExecutedByOptions={
            (navUserExecutedByOptions || []).map((name) => ({ label: name, value: name }))
          }
        />
      </Box>

      {renderTableContent()}
    </PageWrapper>
  );
};


export default SQLDetailsPage;

